import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, CheckCircle2, XCircle } from 'lucide-react';
import { KeeperAssessment, KeeperSignalResult } from '../../types/geminiTypes';
import { cn } from '../../lib/utils';

interface KeeperCardMobileProps {
  assessment: KeeperAssessment;
  onDismiss: () => void;
}

function SignalBadge({ status }: { status: 'KEEP' | 'WATCH' | 'EXIT' | 'N/A' }) {
  return (
    <span className={cn(
      "text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-md border",
      status === 'KEEP' && "bg-success-50 text-success-700 border-success-100",
      status === 'WATCH' && "bg-warning-50 text-warning-700 border-warning-100",
      status === 'EXIT' && "bg-danger-50 text-danger-700 border-danger-100"
    )}>
      {status}
    </span>
  );
}

export function KeeperCardMobile({ assessment, onDismiss }: KeeperCardMobileProps) {
  const verdictColors = {
    KEEP: 'bg-success-50 text-success-700 border-success-100',
    WATCH: 'bg-warning-50 text-warning-700 border-warning-100',
    EXIT: 'bg-danger-50 text-danger-700 border-danger-100',
  };

  return (
    <AnimatePresence>
      <>
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] bg-ink/40 md:hidden"
          onClick={onDismiss}
        />

        {/* Bottom sheet */}
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-[95] md:hidden bg-surface rounded-t-3xl shadow-overlay"
          style={{ maxHeight: '88vh', paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 bg-ink-200 rounded-full" />
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: 'calc(88vh - 24px)' }}>
            <div className="px-5 pb-8 space-y-5">

              {/* Stale warning */}
              {assessment.analysisStale && (
                <div className="bg-warning-50 border border-warning-100 rounded-md px-4 py-3 text-xs text-warning-700 font-medium">
                  ⚠ Based on analysis from {assessment.analysisAge} days ago
                </div>
              )}

              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={cn(
                      "text-xs font-medium uppercase tracking-wider px-3 py-1 rounded-md border",
                      verdictColors[assessment.verdict]
                    )}>
                      {assessment.verdict}
                    </span>
                  </div>
                  <h3 className="font-serif font-medium italic text-lg leading-tight">
                    {assessment.companyName}
                  </h3>
                  <p className="text-xs text-ink-500 mt-0.5">
                    Assessed {new Date(assessment.analysisDate).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={onDismiss}
                  className="w-8 h-8 rounded-md bg-surface-2 flex items-center justify-center shrink-0 mt-1"
                >
                  <X className="w-4 h-4 text-ink-500" />
                </button>
              </div>

              {/* 100-year question */}
              <div className="bg-surface-2 rounded-md p-4">
                <p className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2">
                  The 100-Year Question
                </p>
                <p className="font-serif text-sm text-ink-600 leading-relaxed">
                  {assessment.hundredYearQuestion}
                </p>
              </div>

              {/* Thesis intact */}
              <div className="flex items-start gap-3">
                {assessment.thesisIntact ? (
                  <CheckCircle2 className="w-5 h-5 text-success-600 shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-danger-600 shrink-0 mt-0.5" />
                )}
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-ink-500 mb-0.5">
                    Thesis {assessment.thesisIntact ? 'Intact' : 'Impaired'}
                  </p>
                  <p className="text-sm text-ink-600 leading-relaxed">
                    {assessment.thesisIntactRationale}
                  </p>
                </div>
              </div>

              {/* Verdict rationale */}
              <div>
                <p className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2">
                  Verdict Rationale
                </p>
                <p className="text-sm text-ink-600 leading-relaxed">
                  {assessment.verdictRationale}
                </p>
              </div>

              {/* Signals */}
              <div>
                <p className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-3">
                  Signal Assessment
                </p>
                <div className="space-y-3">
                  {assessment.signals.map((signal: KeeperSignalResult, i: number) => (
                    <div key={i} className="border border-ink-200 rounded-md p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-ink-600">
                          {signal.signalName}
                        </span>
                        <SignalBadge status={signal.status} />
                      </div>
                      <p className="text-[11px] text-ink-500 mb-1">
                        Current: {signal.currentValue}
                      </p>
                      <p className="text-[11px] text-ink-600 leading-relaxed">
                        {signal.rationale}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* What to watch */}
              {assessment.whatToWatch?.length > 0 && (
                <div>
                  <p className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2">
                    What to Watch
                  </p>
                  <ul className="space-y-1.5">
                    {assessment.whatToWatch.map((item: string, i: number) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-ink-600">
                        <span className="text-ink-300 mt-0.5">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Exit triggers */}
              {assessment.exitTriggers?.length > 0 && (
                <div>
                  <p className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2">
                    Exit Triggers
                  </p>
                  <ul className="space-y-1.5">
                    {assessment.exitTriggers.map((item: string, i: number) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-ink-600">
                        <span className="text-danger-600 mt-0.5">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Close button */}
              <button
                onClick={onDismiss}
                className="w-full py-3 rounded-md bg-surface-2 text-sm font-medium text-ink-600 hover:bg-surface transition-colors"
              >
                Close
              </button>

            </div>
          </div>
        </motion.div>
      </>
    </AnimatePresence>
  );
}
